import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../api/client';
import { resolveImage } from './admin/ImageUpload';
import { useSettings } from '../context/SettingsContext';

const HeroBanner = () => {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const { settings } = useSettings() || {};
  // Admin-configurable slide duration. Falls back to 6 s if unset / invalid.
  const durationMs = Math.max(1500, Number(settings?.hero_slide_duration_ms) || 6000);

  useEffect(() => {
    api.get('/hero-slides').then(res => setSlides(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx(i => (i + 1) % slides.length), durationMs);
    return () => clearInterval(t);
  }, [slides.length, durationMs]);

  if (slides.length === 0) {
    return (
      <section className="max-w-[1400px] mx-auto px-4 py-4">
        <div className="rounded-sm bg-neutral-100 h-[280px] md:h-[420px] animate-pulse" />
      </section>
    );
  }

  const slide = slides[idx];

  return (
    <section className="max-w-[1400px] mx-auto px-4 py-4">
      <div className="relative rounded-sm overflow-hidden bg-neutral-100 h-[280px] md:h-[420px]">
        {slides.map((s, i) => (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <img src={resolveImage(s.image)} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
            <div className="relative z-10 h-full flex items-center px-6 md:px-16">
              <div className="max-w-lg text-white">
                <span className="inline-block bg-[#F46A25] text-white text-xs font-bold px-3 py-1 rounded-sm mb-3">{s.tag}</span>
                <h2 className="text-3xl md:text-5xl font-black mb-3 leading-tight">{s.title}</h2>
                <p className="text-base md:text-lg mb-6 text-neutral-100">{s.subtitle}</p>
                <Link to={s.href} className="inline-block bg-white text-[#12263A] hover:bg-[#F46A25] hover:text-white font-bold px-6 py-3 rounded-sm text-sm transition-colors">
                  {s.cta}
                </Link>
              </div>
            </div>
          </div>
        ))}

        {slides.length > 1 && (
          <>
            <button onClick={() => setIdx((idx - 1 + slides.length) % slides.length)} aria-label="Previous" className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-[#12263A] w-10 h-10 rounded-full flex items-center justify-center">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => setIdx((idx + 1) % slides.length)} aria-label="Next" className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-[#12263A] w-10 h-10 rounded-full flex items-center justify-center">
              <ChevronRight className="w-5 h-5" />
            </button>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i+1}`} className={`h-2 rounded-full transition-all ${i === idx ? 'w-8 bg-[#F46A25]' : 'w-2 bg-white/60'}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default HeroBanner;
